import React, { useState } from 'react';
import { useLogoGenerator } from '../../hooks/logo/useLogoGenerator';
import LogoPreview from './LogoPreview';
import LogoControls from './LogoControls';
import LogoTextInput from './LogoTextInput';
import LogoOptions from './LogoOptions';
import BackgroundGenerator from './BackgroundGenerator';

const LogoGenerator: React.FC = () => {
  const [text, setText] = useState('');
  const [isTransparent, setIsTransparent] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState('#3B82F6');
  const [primaryColor, setPrimaryColor] = useState('#FFFFFF');
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [size, setSize] = useState(64);
  const [gradient, setGradient] = useState(true);
  const { handleExport, isExporting } = useLogoGenerator();

  const handleBackgroundExport = async (format: string, style: string) => {
    const element = document.querySelector('.background-preview');
    if (!element) return;

    try {
      if (format === 'svg') {
        // Generate SVG background
        const svgContent = generateBackgroundSVG(style, primaryColor);
        const blob = new Blob([svgContent], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `scalenix-background-${style}.svg`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        // Export PNG using html2canvas
        const canvas = await html2canvas(element);
        const link = document.createElement('a');
        link.download = `scalenix-background-${style}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    } catch (error) {
      console.error('Failed to export background:', error);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          ScaleNix Logo & Background Generator
        </h1>
        
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="space-y-6">
            <LogoTextInput 
              value={text}
              onChange={setText}
            />
            <LogoOptions
              isTransparent={isTransparent}
              onTransparentChange={setIsTransparent}
              backgroundColor={backgroundColor}
              onBackgroundColorChange={setBackgroundColor}
              primaryColor={primaryColor}
              onPrimaryColorChange={setPrimaryColor}
              textColor={textColor}
              onTextColorChange={setTextColor}
              size={size}
              onSizeChange={setSize}
              gradient={gradient}
              onGradientChange={setGradient}
            />
          </div>
          <div className="mt-6">
            <LogoPreview 
              text={text} 
              isTransparent={isTransparent}
              backgroundColor={backgroundColor}
              primaryColor={primaryColor}
              textColor={textColor}
              size={size}
              gradient={gradient}
            />
          </div>
          <LogoControls 
            onExport={handleExport}
            isExporting={isExporting}
          />

          <BackgroundGenerator
            primaryColor={primaryColor}
            onExport={handleBackgroundExport}
          />
        </div>
      </div>
    </div>
  );
};

// Helper function to generate SVG background
const generateBackgroundSVG = (style: string, primaryColor: string): string => {
  const secondaryColor = adjustHue(primaryColor, 40);
  const tertiaryColor = adjustHue(primaryColor, -40);

  const svgPatterns = {
    gradient: `
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 1920 1080">
        <defs>
          <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:${primaryColor};stop-opacity:0.9"/>
            <stop offset="50%" style="stop-color:${secondaryColor};stop-opacity:0.9"/>
            <stop offset="100%" style="stop-color:${tertiaryColor};stop-opacity:0.9"/>
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#grad)"/>
      </svg>
    `,
    waves: `
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 1920 1080">
        <defs>
          <pattern id="waves" width="100" height="100" patternUnits="userSpaceOnUse">
            <path d="M0 50 Q 25 25, 50 50 T 100 50" fill="none" stroke="${tertiaryColor}" stroke-width="2" opacity="0.2"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grad)"/>
        <rect width="100%" height="100%" fill="url(#waves)"/>
      </svg>
    `,
    // Add more patterns as needed
  };

  return svgPatterns[style as keyof typeof svgPatterns] || svgPatterns.gradient;
};

// Helper function to adjust color hue (same as before)
const adjustHue = (color: string, amount: number): string => {
  // Implementation same as in BackgroundGenerator component
  return color; // Placeholder return
};

export default LogoGenerator;